import {
  observable,
  set,
  action,
  computed,
} from 'mobx';
import User from '../User/User';
import Order from '../Order/Order';
import Offer from '../Offer/Offer';
import CartItem, { ICartItem } from '../Cart/CartItem';
import Cart from '../Cart/Cart';
import Address from '../Address';
import { IAcceptedInstallments } from '../../../api/IApiResponses';
import Payment from '../Payment/Payment';
import CreditCard from '../CreditCard/CreditCard';
import Bopis from '../Bopis/Bopis';
import SHIPPING_METHODS from '../../../constants/ShippingMethods';
import CompanyBillingDetails from '../CompanyBillingDetails';
import { ShippingMethodsCollection, IShippingMethod } from '../ShippingMethodsCollection/ShippingMethodsCollection';
import ConvenienceStore from '../ConvenienceStore/ConvenienceStore';
import { IFromTransactionMapping, GiftMessageSettings, GiftWrapSettings } from '../../../api/IDataMappings';
import PAYMENT_PROVIDERS from '../../../constants/PaymentProviders';
import { ADDRESS_TYPE_ID, DeliveryOptions, FIELD_TYPE } from '../../../constants/Constants';
import { IAUPostCollections, IConvenienceStoreProvider, IDropdownOption } from '../Config';

export interface ITransactionPayment {
  id: number;
  paymentName: string;
  paymentType: string;
  billingAddressId: string;
  paymentAmount: number;
  restrictCardNumber: number;
  cardType: string;
  balance: string;
  isReadyToComplete: number;
  giftcardNumber: string;
}

export interface INamedDelivery {
  deliveryDate?: string;
  deliveryTime?: string;
}

export type AvailableDeliveryOptionsMap = {
  [key in DeliveryOptions]?: boolean;
};

export interface IDefaultShipMethod {
  shipMethodId: SHIPPING_METHODS;
  targetCountryId?: number;
}

export interface IDeliveryOptionConfig {
  convenienceStoreProviders: IConvenienceStoreProvider[];
  namedDateOptions: IDropdownOption[];
  namedTimeOptions?: IDropdownOption[];
  AUPostCollections?: IAUPostCollections;
}

export interface IDeliveryOption {
  name: DeliveryOptions;
  shipMethods: IShippingMethod[];
  defaultMethodId: SHIPPING_METHODS;
  config?: IDeliveryOptionConfig;
}

export type ShippingsByDelivery = Partial<Record<DeliveryOptions, IShippingMethod[]>>;

class Transaction {
  @observable transId: number | null = null;

  @observable transKey: string;

  @observable user: User = new User();

  @observable orderSummary: Order = new Order();

  @observable loyaltyPoints: number | string | null = null;

  @observable selectedShippingMethodId: SHIPPING_METHODS;

  @observable payment: Payment = new Payment();

  @observable offers: Offer[] = [];

  @observable productsRemoved: string[] | undefined;

  @observable cart: Cart = new Cart();

  @observable shippingAddress: Address = new Address();

  @observable billingAddress: Address = new Address();

  @observable isExpressFlow: false;

  @observable isAfterpayEligible: boolean;

  @observable afterpayExcludedProducts: boolean;

  @observable creditCards: CreditCard[] = [];

  @observable isBopisOrder = false;

  @observable cardMessage: string;

  @observable giftWrap: number;

  @observable giftMessageSettings: GiftMessageSettings;

  @observable giftWrapSettings: GiftWrapSettings;

  @observable bopis: Bopis = new Bopis();

  @observable convenienceStore: ConvenienceStore = new ConvenienceStore();

  @observable transactionPayments: Partial<ITransactionPayment>[] = [];

  @observable isSalesTaxReady = false;

  @observable companyBillingDetails: CompanyBillingDetails = new CompanyBillingDetails();

  @observable shippingMethods: ShippingMethodsCollection = new ShippingMethodsCollection();

  @observable namedDelivery: INamedDelivery = {};

  @observable acceptedInstallments: IAcceptedInstallments[] = [];

  @observable startScreen: string;

  @observable deliveryTabsDisabled: boolean;

  @observable isPOA: boolean; // Is Phone Order Application

  @observable isDirty = false;

  @observable deliveryOptions?: IDeliveryOption[];

  @observable isFinalSale: boolean;

  // although this might seem redundant, by this we are workaround the requirement to always have a billing address on transaction
  @observable isMissingBilling = false;

  static fromJSON(data: IFromTransactionMapping): Transaction {
    const transaction = new Transaction();
    const offers = data.allAppliedOffers.map((item: Offer) => Offer.fromJSON(item));
    set(transaction, {
      orderSummary: Order.fromJSON(data.orderSummary),
    });
    set(transaction, {
      afterpayExcludedProducts: data.afterpayExcludedProducts,
      cardMessage: data.cardMessage,
      deliveryOptions: data.deliveryOptions,
      deliveryTabsDisabled: data.deliveryTabsDisabled,
      giftMessageSettings: data.giftMessageSettings,
      giftWrap: data.giftWrap,
      giftWrapSettings: data.giftWrapSettings,
      isAfterpayEligible: data.isAfterpayEligible,
      isExpressFlow: data.isExpressFlow,
      isFinalSale: data.isFinalSale,
      isPOA: data.isPOA,
      loyaltyPoints: data.loyaltyPoints,
      payment: Payment.fromJSON({
        giftCards: data.giftCards,
        order: transaction.orderSummary,
        paymentProviders: data.paymentProviders,
        selectedPayment: data.selectedPayment,
        selectedPaymentMode: data.selectedPaymentMode,
      }),
      selectedShippingMethodId: data.selectedShippingMethodId,
      startScreen: data.startScreen,
      store: data.store,
      transId: data.transId,
      transKey: data.transKey,
      transactionPayments: data.transactionPayments,
    });
    set(transaction, { offers });
    set(transaction, {
      cart: Cart.fromJSON({
        cartItems: data.order.cartItems,
      }),
    });
    set(transaction, {
      creditCards: data.savedCreditCards,
    });
    if (data.userEmail) {
      set(transaction, {
        user: User.fromJSON({
          email: data.userEmail,
          firstName: data.firstName,
          hasLoyaltyEnabled: data.userHasLoyaltyEnabled,
          isGuestUser: data.isGuestUser,
          isKeptSignedIn: data.isKeptSignedIn,
          isProCustomer: data.isProCustomer,
          lastName: data.lastName,
          nationalId: data.nationalId,
          recognized: data.recognized,
          registered: data.registered,
          userId: data.userId,
        }),
      });
    }
    if (data.bopisInfo) {
      set(transaction, {
        bopis: Bopis.fromJSON(data.bopisInfo),
        isBopisOrder: data.bopisInfo.isBopisOrder,
      });
    }
    if (data.billToCompany) {
      set(transaction, {
        companyBillingDetails: CompanyBillingDetails.fromJSON(data.billToCompany),
      });
    }
    if (data.acceptedInstallments) {
      set(transaction, {
        acceptedInstallments: data.acceptedInstallments,
      });
    }
    set(transaction, {
      // unfortunately we cannot derive this normally, as the shipping address is not saved
      // on bopis store selection during a transaction load (and it is no expected to be saved)
      isSalesTaxReady: !!(data.shippingAddress && data.shippingAddress[FIELD_TYPE.ID]),
    });
    set(transaction, { convenienceStore: data.convenienceStore });
    set(transaction, {
      namedDelivery: {
        deliveryDate: data.deliveryDate,
        deliveryTime: data.deliveryTime,
      },
    });
    set(transaction, {
      isMissingBilling: !data.billingAddress,
    });

    return transaction;
  }

  @action
  updateOrderSummary = (orderSummary: Order): void => {
    set(this, { orderSummary });
  };

  @action
  updateTransaction = (transaction: Transaction): void => {
    set(this, {
      afterpayExcludedProducts: transaction.afterpayExcludedProducts,
      deliveryOptions: transaction.deliveryOptions,
      deliveryTabsDisabled: transaction.deliveryTabsDisabled,
      isAfterpayEligible: transaction.isAfterpayEligible,
      isExpressFlow: transaction.isExpressFlow,
      isFinalSale: transaction.isFinalSale,
      loyaltyPoints: transaction.loyaltyPoints,
      startScreen: transaction.startScreen,
      transId: transaction.transId,
      transKey: transaction.transKey,
    });
    set(this, { offers: transaction.offers });
    set(this, { user: transaction.user });
    set(this, { cart: transaction.cart });
    set(this, { creditCards: transaction.creditCards });
    set(this, { bopis: transaction.bopis, isBopisOrder: transaction.isBopisOrder });
    set(this, { transactionPayments: transaction.transactionPayments });
    set(this, { companyBillingDetails: transaction.companyBillingDetails });
    set(this, { isSalesTaxReady: transaction.isSalesTaxReady });
    set(this, { isPOA: transaction.isPOA });
    set(this, { acceptedInstallments: transaction.acceptedInstallments });
    set(this, { convenienceStore: transaction.convenienceStore });
    set(this, { namedDelivery: transaction.namedDelivery });
    set(this, { cardMessage: transaction?.cardMessage?.trim() });
    set(this, { giftWrap: transaction.giftWrap });
    set(this, { giftMessageSettings: transaction.giftMessageSettings });
    set(this, { giftWrapSettings: transaction.giftWrapSettings });
    set(this, { isMissingBilling: transaction.isMissingBilling });
    this.payment.update(transaction.payment);
    this.updateOrderSummary(transaction.orderSummary);
  };

  @computed
  get isAvailable(): boolean {
    return Boolean(this.transId);
  }

  @computed
  get hasShippingAddress(): boolean {
    return !this.shippingAddress.isNewAddress;
  }

  @computed
  get isElectronicShipping(): boolean {
    return this.selectedShippingMethodId === SHIPPING_METHODS.ELECTRONIC_SHIPPING;
  }

  @computed
  public get availableShippingOptions(): AvailableDeliveryOptionsMap {
    return this.deliveryOptions.reduce((acc, current) => {
      return {
        ...acc,
        [current.name]: true,
      };
    }, {});
  }

  // This is mainly used to override transaction's shipping options, for shipping calculator/or methods reload by country change...
  @action
  setMethods = (shippingMethods: IShippingMethod[], deliveryOptionName?: DeliveryOptions): void => {
    if (deliveryOptionName) {
      const deliveryOption = this.deliveryOptions.find(item => item.name === deliveryOptionName);
      if (deliveryOption) {
        deliveryOption.shipMethods = shippingMethods;
      }
      // doing this just to make sure the observable item will trigger a re-render
      set(this, { deliveryOptions: [...this.deliveryOptions] });

      return;
    }

    this.shippingMethods.setItems(shippingMethods);
  };

  @action
  setSelectedShippingMethodId = (id: SHIPPING_METHODS): void => {
    this.selectedShippingMethodId = id;
  };

  getShippingMethodById = (id: number): IShippingMethod => {
    return this.deliveryOptions
      ?.find(delivery => delivery.name === DeliveryOptions.HOME_DELIVERY)
      ?.shipMethods?.find(item => item.id === id);
  };

  @action
  setCart = (cartItems: ICartItem[]): void => {
    this.cart = Cart.fromJSON({
      cartItems,
    });
  };

  @action
  setShippingAddress(shippingAddress: Address): void {
    this.shippingAddress = shippingAddress;
  }

  @action
  setBillingAddress(billingAddress: Address): void {
    this.billingAddress = billingAddress;
  }

  @action
  setCompanyBillingDetails(companyBillingDetails: CompanyBillingDetails): void {
    this.companyBillingDetails = companyBillingDetails;
  }

  @action
  setBopisStore(storeId: string): void {
    this.bopis.setStore(storeId);
  }

  @action
  setNamedDeliveryDate(deliveryDate: string): void {
    this.namedDelivery.deliveryDate = deliveryDate;
  }

  @action
  setNamedDeliveryTime(deliveryTime: string): void {
    this.namedDelivery.deliveryTime = deliveryTime;
  }

  @action
  setIsDirty(isDirty: boolean): void {
    this.isDirty = isDirty;
  }

  getTransactionPaymentByProviderName = (
    paymentProviderName: string,
  ): Partial<ITransactionPayment> => this.transactionPayments.find(
    item => item.paymentName === paymentProviderName,
  ) || {};

  getCardByCCUserPaymentId = (
    paymentMethodId: string,
  ): CreditCard => this.creditCards.find(
    item => item.userPaymentMethodId === paymentMethodId,
  );

  getDeliveryOption = (
    shipMethod: SHIPPING_METHODS,
  ): IDeliveryOption | undefined => this.deliveryOptions?.find(
    item => item.shipMethods?.some(method => method.id === shipMethod),
  );

  getDefaultDeliveryMethod = (
    deliveryOption: DeliveryOptions,
  ): SHIPPING_METHODS => {
    return this.deliveryOptions?.find(
      item => item.name === deliveryOption,
    )?.defaultMethodId;
  };

  @computed
  get isTransactionLoaded(): boolean {
    return Boolean(this.transId);
  }

  @computed
  get isZeroPaymentCheckout(): boolean {
    const { ZERO_PAYMENT } = PAYMENT_PROVIDERS;
    return this.orderSummary.totalCost === 0 &&
    !!this.transactionPayments.find(payment => payment.paymentName === ZERO_PAYMENT);
  }


  @computed
  get isBillingAddressRequired(): boolean {
    return !!this.transactionPayments.some(payment => payment.billingAddressId);
  }

  @computed
  get isBillingAddressEligibleForCurrentPayment(): boolean {
    return (
      !(this.billingAddress.isNewAddress
      && this.isBillingAddressRequired)
    );
  }

  @computed
  get shouldResetZeroPayment(): boolean {
    const { ZERO_PAYMENT } = PAYMENT_PROVIDERS;
    const { totalCost } = this.orderSummary;
    const { selectedPayment } = this.payment;
    // If user selects a not free ship method in zero checkout flow.
    return selectedPayment === ZERO_PAYMENT && totalCost > 0;
  }

  @computed
  public get isSameAsShipping(): boolean {
    return JSON.stringify(Address.toJSON(this.shippingAddress))
      === JSON.stringify(Address.toJSON(this.billingAddress));
  }

  @computed
  get deliveryOption(): DeliveryOptions {
    return this.selectedShippingMethodId && this.deliveryOptions?.find(
      item => item.shipMethods.some(method => method.id === this.selectedShippingMethodId),
    )?.name;
  }

  @computed 
  get isGuestOrPOA(): boolean { // To identify orders that do not use saved payments or addresses
    return !!(this.user.isGuestUser || this.isPOA);
  }

  // TODO RCO-8572: can be deleted after RCO-8513 is completed
  @computed
  get isClickAndCollectTrans(): boolean {
    const isStoreAddress = this.shippingAddress?.attributes[FIELD_TYPE.ADDRESS_TYPE] === ADDRESS_TYPE_ID.PACKSTATION;
    const isClickAndCollectMethod = [
      SHIPPING_METHODS.LOCAL_COLLECTION,
      SHIPPING_METHODS.STORE_COLLECTION,
      SHIPPING_METHODS.SELF_COLLECTION,
    ].includes(this.selectedShippingMethodId);

    return isStoreAddress && isClickAndCollectMethod;
  }

  get donations(): CartItem[] {
    return this.cart?.donationItems || [];
  }

  @computed
  get shippingMethodsByDelivery(): ShippingsByDelivery {
    return this.deliveryOptions?.reduce((acc, current) => {
      return {
        ...acc,
        [current.name]: current.shipMethods,
      };
    }, {}) || {};
  }
}

export default Transaction;
  